// File: src/components/PublicHeader.js
// The one navigation bar every public page shows.
//
// Everything lives on the row itself rather than behind a hamburger: the whole
// menu was five items, and hiding five items behind a button costs a click and
// tells a first-time visitor nothing about what the site contains.
//
// Shared by the calendar, the standings and the event pages, so the navigation
// does not drift between them - it used to be a hamburger on one and a single
// "back to standings" button on the others.

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Trophy, Calendar, ListOrdered, LogIn, HelpCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ThemeToggle from './ThemeToggle';
import HelpView from './HelpView';
import {
  LanguageSelect, CountrySelect, HEADER_BUTTON, HEADER_BUTTON_ACTIVE
} from './HeaderControls';

const LINK_ACTIVE = HEADER_BUTTON_ACTIVE;
const LINK_IDLE = HEADER_BUTTON;

function PublicHeader() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [showHelp, setShowHelp] = useState(false);

  // The calendar is the front page, so it answers to both.
  const onCalendar = pathname === '/' || pathname.startsWith('/calendar');
  const onStandings = pathname.startsWith('/standings');

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
        <Link to="/" className="flex items-center gap-3">
          <Trophy className="w-10 h-10 text-accent" />
          <h1 className="text-3xl sm:text-4xl font-bold text-ink">RC Racing Series</h1>
        </Link>

        <div className="flex flex-wrap items-center gap-2">
          <Link to="/" className={onCalendar ? LINK_ACTIVE : LINK_IDLE}>
            <Calendar className="w-4 h-4" />
            {t('calendar')}
          </Link>

          <Link to="/standings" className={onStandings ? LINK_ACTIVE : LINK_IDLE}>
            <ListOrdered className="w-4 h-4" />
            {t('standings')}
          </Link>

          <LanguageSelect />
          <CountrySelect />

          <button
            onClick={() => setShowHelp(true)}
            className={LINK_IDLE}
            title={t('help')}
            aria-label={t('help')}
          >
            <HelpCircle className="w-4 h-4" />
          </button>

          <Link to="/login" className={LINK_IDLE}>
            <LogIn className="w-4 h-4" />
            {t('login')}
          </Link>

          <ThemeToggle />
        </div>
      </div>

      {showHelp && <HelpView onClose={() => setShowHelp(false)} isAdmin={false} />}
    </>
  );
}

export default PublicHeader;
